/**
 * AudioProgress Component
 *
 * Progress indicator for audio generation with elapsed time tracking.
 */

import { memo } from 'react';
import { Zap } from 'lucide-react';
import { ElapsedTimeDisplay } from '@/components/common';
import { useTranslation } from '@/hooks/useTranslation';
import type { GenerationProgress } from '@/types';

interface AudioProgressProps {
  /** Full progress data including timing (preferred) */
  progress: GenerationProgress | null;
}

export const AudioProgress = memo(function AudioProgress({ progress }: AudioProgressProps) {
  const { t } = useTranslation();

  // Handle null progress gracefully
  if (!progress) {
    return null;
  }

  const progressValue = progress.progress ?? 0;
  const message = progress.message || t('progress.processing');

  // Get stage-based message or use backend message
  const getStatusMessage = (): string => {
    if (message && message !== 'Processing audio...') {
      return message;
    }
    if (progressValue < 20) {
      return t('progress.initializing');
    }
    if (progressValue < 90) {
      return t('progress.processing');
    }
    return t('progress.stages.finalizing');
  };

  return (
    <div className="card p-4 sm:p-6" data-testid="progress-container">
      <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4 flex items-center space-x-2">
        <Zap className="h-5 w-5 text-purple-600" />
        <span>{t('progress.messages.generatingAudio')}</span>
      </h3>
      <div className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-secondary-600 dark:text-secondary-400">
            <span>{t('common.progress')}</span>
            <span>{Math.round(progressValue)}%</span>
          </div>
          <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-600 to-pink-600 h-full rounded-full transition-all duration-500"
              style={{ width: `${progressValue}%` }}
            />
          </div>
        </div>

        {/* Status Message */}
        <div className="flex items-center space-x-2 text-secondary-700 dark:text-secondary-300">
          <div className="spinner" />
          <span>{getStatusMessage()}</span>
        </div>

        {/* Elapsed Time Display */}
        {(progress.started_at || progress.elapsed_seconds !== undefined) && (
          <div className="mt-3">
            <ElapsedTimeDisplay
              startedAt={progress.started_at ?? null}
              serverElapsedSeconds={progress.elapsed_seconds ?? null}
              estimatedRemainingSeconds={progress.estimated_remaining_seconds ?? null}
              showEstimate={true}
              size="sm"
            />
          </div>
        )}
      </div>
    </div>
  );
});
